import { useReducer, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import enhancementPackages from '../../helpers/enhancementPackages';
import DisplayEnhancementPackage from '../../components/actions/DisplayEnhancementPackage';
import Button from '../../components/inputs/Button';
import {
  initialState,
  reducer,
  START_ACTION,
  CLEAR_SUCCESS,
} from '../../reducers/actionReducer';
import ActionModal from '../../components/ActionModal';
import ToastNotification from '../../components/ToastNotification';
import { useAuth } from '../../hooks/useAuth';
import { useOrder } from '../../hooks/useOrder';
import { calculateEnhancementPackageRate } from '../../helpers/pricing';
import { DEFAULT_ENHANCEMENT_PACKAGES_QUERY } from '../../queries/packageQueries';
import classNames from 'classnames';
import AudienceIcon from '../../components/icons/AudienceIcon';
import { useHover } from 'react-use';
import { isEnhancementEnabled } from '../../utils/featureFlags';
import PermissionModal from '../../components/PermissionModal';

const transitionClasses = 'transition-all duration-100';

type PackageTileProps = {
  packageName: string;
  productKey?: string;
  rate: number;
  descriptionShort: string;
  descriptionLong: string;
  variablesIncluded: string[];
  additionalDescription?: JSX.Element;
  includesCorePackage?: boolean;
  componentType: string;
  dispatchAction: any;
  icon: JSX.Element;
  plan: string;
  onClick?: () => void;
  openOrder?: any;
};

function PackageTile({
  packageName,
  productKey,
  rate,
  descriptionShort,
  descriptionLong,
  variablesIncluded,
  additionalDescription,
  componentType,
  dispatchAction,
  icon,
  plan,
  onClick,
  openOrder,
}: PackageTileProps) {
  function handlePackageClick(packageName) {
    dispatchAction({
      type: START_ACTION,
      payload: {
        name: packageName,
        continueLabel: componentType === 'custom' ? 'Submit' : 'Select Package',
        component: (
          <DisplayEnhancementPackage
            packageName={packageName}
            productKey={productKey}
            descriptionLong={descriptionLong}
            variablesIncluded={variablesIncluded}
            additionalDescription={additionalDescription}
            componentType={componentType}
            matchedEnhancement={null}
            dispatchAction={dispatchAction}
            rate={calculateEnhancementPackageRate(rate, plan)}
            openOrder={openOrder}
          />
        ),
      },
    });
  }

  const element = (hovered) => (
    <div
      className={`flex flex-col justify-between flex-shrink rounded-half bg-gray-100 p-5 cursor-pointer filter hover:drop-shadow-xl hover:bg-gray-200 ${transitionClasses}`}
      onClick={() =>
        typeof onClick === 'function'
          ? onClick()
          : handlePackageClick(packageName)
      }
    >
      <div>
        <div
          className={classNames(
            'mb-3 h-3 2xl:h-auto flex items-center gap-1',
            hovered && 'text-secondary',
            !hovered && 'text-primary'
          )}
        >
          {icon}
        </div>
        <h4 className="mb-1 2xl:mb-3 text-lg 2xl:text-xl">{packageName}</h4>
      </div>
      <p className="text-sm 2xl:text-base">{descriptionShort}</p>
    </div>
  );

  const [hoverable] = useHover(element);

  return hoverable;
}

export default function AudiencePackages() {
  const [actionState, dispatchAction] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openOrder } = useOrder();
  const isEnhancementFeatureEnabled = isEnhancementEnabled();

  const audienceData = location.state?.orderDetails;

  // Fetch default enhancement packages from backend
  const { data: defaultPkgsData } = useQuery(DEFAULT_ENHANCEMENT_PACKAGES_QUERY, {
    fetchPolicy: 'cache-first',
  });

  // Create alias map for package name normalization
  const aliasMap = { Foundation: 'Foundations' };

  // Merge local helper data with backend variables
  const mergedPackages = enhancementPackages.map((pkg) => {
    // Handle "Foundation" -> "Foundations" aliasing
    const backendPackageName = aliasMap[pkg.packageName] || pkg.packageName;
    const backendPkg = defaultPkgsData?.defaultEnhancementPackages?.find(
      (p: any) => p?.packageName === backendPackageName
    );

    // Merge backend variables with local package
    return {
      ...pkg,
      variablesIncluded: backendPkg?.variablesIncluded ?? pkg.variablesIncluded,
    };
  });

  useEffect(() => {
    if (!audienceData?.audienceId) {
      navigate('../../mywork/orders');
    }
  }, [audienceData, navigate]);

  useEffect(() => {
    if (actionState.success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      );
    }
  }, [actionState.success]);

  return (
    <>
      <div className="w-full 2xl:w-10/12 h-full flex flex-col px-5 pt-5">
        <h3 className="mb-2">Enhancement Packages</h3>
        <div className="flex justify-between">
          <div className="flex flex-col flex-1">
            <p className="pb-2">
              Select the ideal package of elements to append to your purchased
              audience to unlock personalization, segmentation, and new methods
              of outreach.
            </p>
          </div>
          <div className="flex flex-col flex-1 justify-end items-end">
            {audienceData && (
              <div className="flex flex-col">
                <p>
                  Audience:{' '}
                  <span className="font-bold">{audienceData.audienceName}</span>
                </p>
                <p>
                  Records:{' '}
                  <span className="font-bold">
                    {audienceData.recordsIncluded?.toLocaleString()}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 grid-rows-3 gap-3 2xl:gap-5 py-3 2xl-py-5">
          {enhancementPackages.map((enhancementPackage) => (
            <PackageTile
              {...enhancementPackage}
              dispatchAction={dispatchAction}
              key={enhancementPackage.packageName}
              plan={user?.plan}
              openOrder={openOrder}
            />
          ))}
          <PackageTile
            dispatchAction={dispatchAction}
            packageName="Custom"
            rate={null}
            descriptionShort="Build your own enhancement package"
            descriptionLong="Select the elements you want to append to your purchased audience to unlock personalization, segmentation, and new methods of outreach."
            variablesIncluded={[]}
            componentType="custom"
            icon={<AudienceIcon />}
            plan={user?.plan}
            onClick={() => navigate('../custom', { state: location.state })}
            openOrder={openOrder}
          />
        </div>
        {actionState.inProgress && (
          <ActionModal
            actionState={actionState}
            dispatchAction={dispatchAction}
            show={actionState.inProgress}
          />
        )}
        <ToastNotification
          message={actionState.success}
          success={true}
          show={actionState.success}
        />
      </div>
      {!isEnhancementFeatureEnabled && <PermissionModal module="Enhancement" />}
    </>
  );
}
